// src/lib/intake/reviewModel.js
//
// Ticket 066 (Advisor Intake I6). Pure: what the Review screen shows, from the
// answers the page already holds. No React, no network, nothing stored.
//
// It REUSES the per-screen completeness checks the Next button and resume use
// (section1Facts.isScreenComplete, section23Facts.isScreenComplete23) and adds only
// the reads those checks do not make: a stored "Not sure" on an allow-listed
// Section 1 fact, and a skipped income. The server's computeCompleteness is
// TypeScript and cannot run in the browser; reviewParity.test.ts (Deno) and
// reviewModel.test.js (Jest) run one shared fixture through both to keep them honest.
//
// Row statuses (the five-status vocabulary, in plain words):
//   answered | none | not_applicable | open | not_sure | skipped
// A GAP is a row that is open, not_sure or skipped. Gaps are listed with a link
// back to the screen (fixScreen). Facts the intake never asks are never gaps
// (see askedFacts.js).

import { SECTION1_SCREEN_ORDER, REVIEW_STATUS_WORDS, INTAKE_ASKED_FACTS, ALL_GROUP_KEYS } from './intakeConfig'
import { isScreenComplete, applicableKeys } from './section1Facts'
import {
  isScreenComplete23, shouldShowScreen, touchedIn,
} from './section23Facts'

const GAP_STATUSES = ['open', 'not_sure', 'skipped']
// Screens with no Fix/Edit link: the tax year (a different year is a different
// saved intake) and the advice screen.
const NO_LINK = ['1.1', '3.4']

const unknownFact = (facts, key) => !!facts[key] && facts[key].status === 'unknown'
const zeroFact = (facts, key) => !!facts[key] && facts[key].status === 'known_zero'

// Section 1 facts whose stored "Not sure" the person may revisit (kept from 061).
const NOT_SURE_KEYS = {
  '1.4': ['eu_citizen'],
  '1.5': ['asset_split', 'spouse_citizenship_status', 'spouse_eu_citizen'],
}

function section1Status(id, ctx) {
  const { answers } = ctx
  if (id === '1.1') return ctx.taxYear != null ? 'answered' : 'open'
  if (!isScreenComplete(id, ctx)) return 'open'
  const applies = applicableKeys(answers.facts)
  const sure = (NOT_SURE_KEYS[id] || []).some((key) => applies[key] && unknownFact(answers.facts, key))
  if (sure) return 'not_sure'
  if (id === '1.7') {
    const f = answers.facts
    const noDisability = !!f.disability_taxpayer && f.disability_taxpayer.status === 'known_value' && f.disability_taxpayer.value === false
    if (zeroFact(f, 'children_under_25') && zeroFact(f, 'ascendants_over_65') && noDisability) return 'none'
  }
  return 'answered'
}

function section23Status(id, ctx) {
  const { answers } = ctx
  const show = shouldShowScreen(id, ctx)
  switch (id) {
    case '2.1': {
      if (!isScreenComplete23(id, ctx)) return 'open'
      const noEvents = Object.values(answers.events || {}).every((v) => v !== true)
      return noEvents && answers.ownership === null ? 'none' : 'answered'
    }
    case '2.2':
      if (answers.skipIncome) return 'skipped'
      if (!isScreenComplete23(id, ctx)) return 'open'
      return answers.none.self ? 'none' : 'answered'
    case '2.3':
      if (!show) return 'not_applicable'
      return isScreenComplete23(id, ctx) ? 'answered' : 'open'
    case '2.3s':
      if (!show) return 'not_applicable'
      // The filer skipped income, so the spouse page may be empty: also a gap.
      if (answers.skipIncome && touchedIn(answers, 'spouse', ALL_GROUP_KEYS).length === 0) return 'skipped'
      return isScreenComplete23(id, ctx) ? 'answered' : 'open'
    case '2.4':
    case '3.3':
      if (!show) return 'not_applicable'
      return isScreenComplete23(id, ctx) ? 'answered' : 'open'
    case '3.4':
      return show ? 'answered' : 'not_applicable'
    default:
      return isScreenComplete23(id, ctx) ? 'answered' : 'open'
  }
}

// A skipped spouse page is fixed on the filer's income page (2.2).
const fixScreenOf = (id, status) => (id === '2.3s' && status === 'skipped' ? '2.2' : id)

const SECTION23_ROWS = ['2.1', '2.2', '2.3', '2.3s', '2.4', '2.5', '3.3', '3.4', '4.1']

// ctx = { answers, taxYear, today, suggestions, dismissed }
export function buildReview(input) {
  const { answers, taxYear = null, today = new Date(), suggestions = null, dismissed = [] } = input
  const ctx = { answers, taxYear, yearOptions: taxYear == null ? [] : [taxYear], today, suggestions, dismissed }

  const rows = []
  for (const id of SECTION1_SCREEN_ORDER) {
    rows.push(makeRow(id, 1, section1Status(id, ctx), ctx))
  }
  for (const id of SECTION23_ROWS) {
    rows.push(makeRow(id, 2, section23Status(id, ctx), ctx))
  }

  const gaps = rows
    .filter((r) => GAP_STATUSES.includes(r.status))
    .map((r) => ({ screen: r.screen, fixScreen: r.fixScreen, status: r.status, word: r.word, facts: INTAKE_ASKED_FACTS[r.screen] || [] }))

  const counts = { answered: 0, none: 0, not_applicable: 0, open: 0, not_sure: 0, skipped: 0 }
  for (const r of rows) counts[r.status] += 1

  return { rows, gaps, counts, taxYear }
}

function makeRow(id, section, status, ctx) {
  const row = {
    screen: id,
    section,
    status,
    word: REVIEW_STATUS_WORDS[status],
    fixScreen: fixScreenOf(id, status),
    link: NO_LINK.includes(id) || status === 'not_applicable' ? null : GAP_STATUSES.includes(status) ? 'fix' : 'edit',
  }
  if (id === '1.1' && ctx.taxYear != null) row.detail = String(ctx.taxYear)
  return row
}

// Ticket 069 (D12): the exceptions callout on top of Review, from the gaps buildReview
// already finds. No new check logic. "Needs an answer" = open; "Not sure or skipped" =
// not_sure + skipped.
export function calloutGroups(review) {
  return {
    needs: review.gaps.filter((g) => g.status === 'open'),
    unsure: review.gaps.filter((g) => g.status === 'not_sure' || g.status === 'skipped'),
  }
}
